/**
 *
 * AttendanceControl
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import classNames from 'classnames';
import moment from 'moment';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import useDeviceDetect from 'utils/useDeviceDetect';

// styles
import './style.scss';

import {
  flushStateAttendanceControl,
  loadAttendance,
  changeAttendance,
} from './actions';
import {
  selectAttendanceData,
  selectAttendanceLoading,
  selectAttendanceError,
  selectAttendanceLoadingChange,
} from './selectors';

// antd component
import { Button, Modal } from 'antd';

// assets
import {
  ArrowLeftOutlined,
  CloseOutlined,
  MoreOutlined,
} from '@ant-design/icons';
//import CustomIcons from 'components/CustomIcons';

// components
import ModalChangePresence from 'mobile/components/ModalChangePresence';
import StepByStepWrapper from 'mobile/components/StepByStepWrapper';
import PassControlSelection from 'mobile/components/PassControlSelection';
import ExcusedStudents from 'mobile/components/ExcusedStudents';
import ControlStdPresens from 'mobile/components/ControlStdPresens';
import ResumeControlPresens from 'mobile/components/ResumeControlPresens';
// cta notifications
import FinalControlScreen from 'mobile/components/FinalControlScreen';

// global user
import { useGetLocalUser } from 'engine/Contexts/User.context';

// utils
import {
  clean,
  getUniqueArrayObj,
  getObjId,
} from 'utils/helpers/helper.general';
import { isSameDates } from 'utils/moment/moment.utils';
import {
  getExcusedAbsences,
  getExcused,
  getAttendancesOnDate,
} from 'utils/helpers/helper.generateData';

export function AttendanceControl({
  // props
  course,
  onDate,
  updateCourse,
  loadingUpdates,
  // states
  dateFormat,
  history,
  attendanceData,
  loading,
  loadingCnahge,
  // actions
  flushState,
  loadAttendance,
  changeAttendance,
  // default
  mode,
  className,
}) {
  useInjectReducer({ key: 'attendanceControl', reducer });
  useInjectSaga({ key: 'attendanceControl', saga });

  const childClassNames = classNames('attendance-control-container', className);
  const { isMobile } = useDeviceDetect();

  const localUser = useGetLocalUser()();

  const [controlType, setControlType] = useState('all'); // all, check-absent, check-present

  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [step, setStep] = useState(1);
  const [totalStep, setTotalStep] = useState(3);
  const [showFinal, setShowFinal] = useState(false);

  const initAttendance = {
    _id: null,
    course: course?._id || null,
    date: onDate,
    absences: [],
    missing_students: [],
    students: [],
  };
  const [currentAttendance, setCurrentAttendance] = useState(initAttendance);

  /**
   * Loading Course data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // Clearing the state after unmounting a component
    // Anything in here is fired on component unmount.
    return () => {
      flushState();
    };
  }, []);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (course?._id) {
      const currentAttendanceArr = getAttendancesOnDate(course, onDate);
      const newData = {
        course: course?._id,
        absences: getExcusedAbsences(course?.students, onDate, course),
        missing_students: getExcused(course?.students, onDate, course),
      };
      setCurrentAttendance({
        ...currentAttendance,
        ...newData,
      });

      if (currentAttendanceArr && currentAttendanceArr.length > 0) {
        setCurrentAttendance(currentAttendanceArr[0]);
      } else {
        setCurrentAttendance({
          ...initAttendance,
          ...newData,
        });
      }
    } else {
      setCurrentAttendance(initAttendance);
    }
  }, [course, onDate]);

  // Refresh current course attendances list
  useEffect(() => {
    if (!loadingCnahge && attendanceData) {
      if (isSameDates(attendanceData?.date, currentAttendance?.date)) {
        setCurrentAttendance(attendanceData);
      }

      // refresh current course attendances list
      if (
        course.hasOwnProperty('attendances') &&
        course?.attendances.length > 0
      ) {
        const hasDate = getAttendancesOnDate(course, attendanceData?.date);

        let attend = course?.attendances;
        if (hasDate && hasDate.length > 0) {
          attend = course?.attendances.map(att => {
            if (
              isSameDates(attendanceData?.date, att?.date) &&
              getObjId(att?.course) == course?._id
            ) {
              return { ...att, ...attendanceData };
            } else {
              return att;
            }
          });
        } else {
          attend = getUniqueArrayObj([...attend, attendanceData], '_id');
        }

        updateCourse({
          ...course,
          attendances: attend,
        });
      } else {
        updateCourse({
          ...course,
          attendances: [attendanceData],
        });
      }
    }
  }, [attendanceData]);

  const closeModal = useCallback(() => {
    setShowFinal(false);
    setIsOpenModal(false);
    setControlType('all');
    // ----
    setStep(1);
    setCurrentAttendance(initAttendance);
    if (mode == 'attendance') {
      updateCourse(false);
    }
  }, [initAttendance]);
  const openAlert = useCallback(() => {
    setIsOpenAlert(true);
    setIsOpenModal(false);
  }, []);
  const openAttendanceModal = useCallback(() => {
    setIsOpenAlert(false);
    setIsOpenModal(true);
  }, []);
  const alertOk = useCallback(() => {
    openAttendanceModal();
  }, []);
  const alertCancel = useCallback(() => {
    setIsOpenAlert(false);
    setCurrentAttendance(initAttendance);
    setShowFinal(false);
    setIsOpenModal(false);
    setControlType('all');
    // ----
    setStep(1);
    if (mode == 'attendance') {
      updateCourse(false);
    }
  }, [initAttendance]);

  const openModalCallback = useCallback(() => {
    setStep(1);
    setShowFinal(false);
    if (currentAttendance?._id) {
      openAlert();
    } else {
      openAttendanceModal();
    }
  }, [course, currentAttendance]);

  // default form options
  let formOptions = {
    formData: currentAttendance || initAttendance,
    course: course,
    currentStep: step,
    // Step Navigation
    nextStep: () => step != totalStep && setStep(step + 1),
    prevStep: () => {
      return step > 1 && setStep(step - 1);
    },
    goToStep: number => setStep(number),
    // other
    controlType: controlType,
    changeControlType: type => setControlType(type),
    result: values => resultData(values),
  };

  // Main result/save data
  const resultData = useCallback(
    result => {
      const newObj = { ...currentAttendance, ...result };
      setCurrentAttendance(newObj);
      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
        changeAttendance(clean(newObj));
      }
    },
    [currentAttendance, localUser, step, totalStep],
  );

  const finalNotification = useMemo(() => {
    return (
      <FinalControlScreen loading={loadingCnahge} continueAction={closeModal} />
    );
  }, [loadingCnahge, closeModal]);

  const controlSteps = useCallback(
    stepNumber => {
      const courseTitle = `${course?.subject?.name}${
        course?.class_info?.title ? ' (' + course?.class_info?.title + ')' : ''
      }${onDate ? ' - ' + moment(onDate).format(dateFormat) : ''}`;
      switch (stepNumber) {
        case 1:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={1}
                  isActive={step == 1}
                  header={`Contrôle des présences`}
                  title={courseTitle}
                >
                  <PassControlSelection {...formOptions} />
                  <ExcusedStudents
                    course={course}
                    dataSource={course?.students || []}
                    onDate={onDate}
                  />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 2:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={2}
                  isActive={step == 2}
                  header={`Contrôle des présences`}
                  title={courseTitle}
                >
                  <ControlStdPresens {...formOptions} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 3:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={3}
                  isActive={step == 3}
                  header={`Contrôle des présences`}
                  title={'Liste des absents'}
                >
                  <ResumeControlPresens {...formOptions} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
      }
    },
    [showFinal, formOptions, step, course, onDate],
  );

  const renderModal = useMemo(() => {
    return (
      <Modal
        className="attendance-control"
        title={
          <>
            {isMobile && (
              <>
                {/* <MoreOutlined className="attendance-control__mobile-menu" /> */}
                <div className="attendance-control__mobile-menu" />
              </>
            )}
            {'Contrôle des présences'}
          </>
        }
        open={isOpenModal}
        closeIcon={
          <span className="attendance-control__close" onClick={closeModal}>
            {!isMobile ? (
              <>
                <CloseOutlined /> Close
              </>
            ) : (
              <ArrowLeftOutlined />
            )}
          </span>
        }
        onCancel={closeModal}
        footer={[]}
      >
        {controlSteps(step)}
      </Modal>
    );
  }, [step, isMobile, isOpenModal, closeModal]);

  const renderBtn = useMemo(
    () => (
      <Button
        className={`ant-btn-submit ${!!!course ? 'disabled' : ''}`}
        disabled={!!!course}
        loading={loadingUpdates}
        onClick={openModalCallback}
      >
        Contrôle des présences
      </Button>
    ),
    [course, openModalCallback, loadingUpdates],
  );

  // render function
  return (
    <div className={childClassNames}>
      {renderBtn}
      <ModalChangePresence
        isOpen={isOpenAlert}
        setOpen={setIsOpenAlert}
        okHandle={alertOk}
        cancelHandle={alertCancel}
      />
      {renderModal}
    </div>
  );
}

AttendanceControl.defaultProps = {
  mode: 'attendance',
  dateFormat: 'DD.MM.YYYY',
};
AttendanceControl.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingCnahge: PropTypes.bool,
  loadAttendance: PropTypes.func.isRequired,
  changeAttendance: PropTypes.func.isRequired,
  // props
  course: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  onDate: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
    PropTypes.bool.isRequired,
  ]),
  mode: PropTypes.oneOf(['course', 'attendance']),
};

const mapStateToProps = createStructuredSelector({
  loading: selectAttendanceLoading(),
  loadingCnahge: selectAttendanceLoadingChange(),
  error: selectAttendanceError(),
  attendanceData: selectAttendanceData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushStateAttendanceControl()),
    loadAttendance: id => dispatch(loadAttendance(id)),
    changeAttendance: attendanceData =>
      dispatch(changeAttendance(attendanceData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AttendanceControl);
